import React, {useEffect, useRef, useState} from "react";
import './../Purchase.scss';
import './../PurchasePage.scss';
import {useHistory} from "react-router";
import PurchaseStatus from "../PurchaseStatus";
import Strings from "../../../../config/Strings";
import GlobalConstants from "../../../../config/GlobalConstants";
import {authCheck} from "../../../../util/authCheck";
import creditCard from "../../../../../assets/images/creditcard.png";
import debit from "../../../../../assets/images/maestro.png";
import {ConfirmationModal, InfoModal} from "../../../../global/ConfirmationModal";
import googleTagManagerWrapper from "../../../../api/GoogleTagManagerWrapper";
import {GetBillwerkAccountByOwner} from "../../../premium/PremiumService";
import IBAN from "iban";
import {GetCurrentAccount} from "../../../dashboard/DashboardService";
import {CreateResource} from "../../../../api/SpineClient";
import AddressForm from "../../../../global/address-form/AddressForm";
import AddressValidationModal from "../../../../global/address-form/AddressValidationModal";
import {
    fromValidationSuggestion,
    hasErrors,
    moveHouseNumberFromStreet,
    toValidationRequest,
    validateAddress,
    validateAddressField
} from "../../../../global/address-form/AddressValidation";

//direct debit is currently not offered, the form is kept to switch it on again
const DEBIT_AVAILABLE = false;

const FIELD_LABELS = {
    country: Strings.PURCHASE_CONTACT_COUNTRY,
    firstName: Strings.PURCHASE_CONTACT_FIRSTNAME,
    lastName: Strings.PURCHASE_CONTACT_LASTNAME,
    street: Strings.PURCHASE_CONTACT_STREET,
    houseNumber: Strings.PURCHASE_ADDRESS_HOUSE_NO_LONG,
    zip: Strings.PURCHASE_CONTACT_POSTAL_CODE,
    city: Strings.PURCHASE_CONTACT_CITY,
    accountHolder: Strings.PURCHASE_SUBSCRIPTION_DEBIT_OWNER,
    iban: Strings.PURCHASE_SUBSCRIPTION_DEBIT_IBAN,
    sepaAccepted: Strings.PURCHASE_SUBSCRIPTION_DEBIT_AGB_TITLE,
};

//the fields of the address validation (purchase-service) and of the address form
const VALIDATION_FIELDS = {STREET: "street", HOUSE_NUMBER: "houseNumber", POSTAL_CODE: "zip", CITY: "city", COUNTRY: "country"};

//identifies an address, so an address the customer already checked is not validated again
const addressKey = (address) => [address.street, address.houseNumber, address.zip, address.city, address.country]
    .map(value => (value || "").trim().toLowerCase())
    .join("|");

export default function SubscriptionPurchasePayment() {

    const [purchaseData, setPurchaseData] = useState(JSON.parse(sessionStorage.getItem('ls.purchase')));
    const [paymentMethod, setPaymentMethod] = useState(purchaseData != null ? purchaseData.paymentMethod : null);
    const [contact, setContact] = useState((purchaseData != null && purchaseData.contact != null) ? purchaseData.contact : {country: "DE"});
    const [debitForm, setDebitForm] = useState((purchaseData != null && purchaseData.paymentData != null) ?
        purchaseData.paymentData : {accountHolder: '', iban: '', bic: '', sepaAccepted: false});
    const [email] = useState(JSON.parse(sessionStorage.getItem('email')));

    const [errors, setErrors] = useState({});
    const [warnings, setWarnings] = useState({});
    const [paymentMethodMissing, setPaymentMethodMissing] = useState(false);
    const [showErrorSummary, setShowErrorSummary] = useState(false);
    const [validatingAddress, setValidatingAddress] = useState(false);
    const [addressValidation, setAddressValidation] = useState(null);
    const fieldToFocus = useRef(null);

    const [showLoginHint, setShowLoginHint] = useState(false);
    const [showPlanPurchaseError, setShowPlanPurchaseError] = useState(false);
    const [showCustomerLockedError, setShowCustomerLockedError] = useState(false);

    const history = useHistory();
    const bundled = purchaseData != null && !!purchaseData.selectedColor;

    document.title = Strings.PURCHASE_SUBSCRIPTION_BROWSER_TAB;

    const isPermittedCountry = (country) => purchaseData.premiumAccessFeature.purchasePermissions.territories.includes(country);

    useEffect(() => {
        const loadBillwerkData = () => {
            GetCurrentAccount().then(response => {
                let accountId = response.result.id;
                GetBillwerkAccountByOwner(accountId).then(billwerkResponse => {
                    let dataBillwerk = billwerkResponse.result;
                    if (dataBillwerk) {
                        setContact(prevState => ({
                            ...prevState,
                            firstName: dataBillwerk.FirstName || '',
                            lastName: dataBillwerk.LastName || '',
                            email: dataBillwerk.EmailAddress || '',
                            street: dataBillwerk.Address?.Street || '',
                            houseNumber: dataBillwerk.Address?.HouseNumber || '',
                            zip: dataBillwerk.Address?.PostalCode || '',
                            city: dataBillwerk.Address?.City || '',
                            country: dataBillwerk.Address?.Country || 'DE',
                            customerIsLocked: dataBillwerk.IsLocked || false
                        }));
                    }
                    if (dataBillwerk?.IsLocked) {
                        setShowCustomerLockedError(true);
                    }
                });
            }).catch(error => console.error("Error loading Billwerk data:", error));
        };

        if (!purchaseData || !purchaseData.contact) {
            loadBillwerkData()
        }

        if (!authCheck()) {
            //Set the max step to 2 and proceed
            purchaseData.maxStep = 1;
            sessionStorage.setItem('ls.purchase', JSON.stringify(purchaseData));
            history.push("/" + GlobalConstants.APP_PATH + "purchase/subscription/authenticate");
        }

        const checkIfPlanVariantIsAvailableForPurchase = (premiumAccessFeature) => {
            let countryPermissions = premiumAccessFeature.purchasePermissions.territories;
            const permissionDe = countryPermissions.includes("DE");
            const permissionCh = countryPermissions.includes("CH");
            const permissionAt = countryPermissions.includes("AT");

            let webPermissions = premiumAccessFeature.purchasePermissions.platforms.includes("WEB");

            let appPermissions = premiumAccessFeature.purchasePermissions.apps.includes("TIGERTONES");

            if ((!permissionDe && !permissionCh && !permissionAt) || !webPermissions || !appPermissions) {
                setShowPlanPurchaseError(true);
            }
        }

        checkIfPlanVariantIsAvailableForPurchase(purchaseData.premiumAccessFeature);

    }, [history, purchaseData]);

    const paymentMethods = [
        {
            id: GlobalConstants.PAYMENT_METHOD_PAYPAL,
            name: Strings.PURCHASE_TICKET_PAYMENT_METHOD_PAYPAL,
            image: "https://tiger.media/cdn/shopifycloud/checkout-web/assets/c1/assets/paypal-logo.Q2f7XzPy.svg",
        },
        {
            id: GlobalConstants.PAYMENT_METHOD_CREDIT_CARD,
            name: Strings.PURCHASE_PAYMENT_METHOD_CC,
            image: creditCard,
        },
        //no direct debit for bundles and in switzerland
        ...(DEBIT_AVAILABLE && !bundled && contact.country !== "CH" ? [{
            id: GlobalConstants.PAYMENT_METHOD_DEBIT,
            name: Strings.PURCHASE_SUBSCRIPTION_PAYMENT_METHOD_DEBIT,
            image: debit,
        }] : []),
    ];

    const countryError = (country) => isPermittedCountry(country) ? null : Strings.PURCHASE_CONFIRMATION_PERMISSION_COUNTRY_ERROR;

    const updateContact = (changes) => {
        const updatedContact = {...contact, ...changes};
        setContact(updatedContact);

        //fields with an error are checked again while typing, warnings of the address validation are obsolete
        const changedFields = Object.keys(changes);
        setErrors(prevErrors => {
            const updatedErrors = {...prevErrors};
            changedFields.filter(field => prevErrors[field]).forEach(field => {
                updatedErrors[field] = validateAddressField(field, updatedContact);
            });
            if (changes.country) {
                updatedErrors.country = countryError(changes.country);
                if (updatedContact.zip) {
                    updatedErrors.zip = validateAddressField("zip", updatedContact);
                }
            }
            return updatedErrors;
        });
        setWarnings(prevWarnings => {
            const updatedWarnings = {...prevWarnings};
            changedFields.forEach(field => delete updatedWarnings[field]);
            return updatedWarnings;
        });
        if (changes.country === "CH" && paymentMethod === GlobalConstants.PAYMENT_METHOD_DEBIT) {
            setPaymentMethod(null);
        }
    }

    const validateContactField = (field) => {
        let validatedContact = contact;
        if (field === "street") {
            const movedHouseNumber = moveHouseNumberFromStreet(contact);
            if (movedHouseNumber) {
                validatedContact = {...contact, ...movedHouseNumber};
                setContact(validatedContact);
            }
        }
        //only filled fields, so leaving an empty field doesn't show errors before the customer is done
        if (!(validatedContact[field] || "").trim() && !errors[field]) {
            return;
        }
        setErrors(prevErrors => ({
            ...prevErrors,
            [field]: validateAddressField(field, validatedContact),
            ...(field === "street" && validatedContact.houseNumber !== contact.houseNumber
                ? {houseNumber: validateAddressField("houseNumber", validatedContact)} : {})
        }));
    }

    const updatePaymentMethod = (method) => {
        setPaymentMethod(method);
        setPaymentMethodMissing(false);
        googleTagManagerWrapper.paymentMethodChangedEvent(purchaseData, method);
    }

    const updateDebitForm = (field, value) => {
        setDebitForm(prevState => ({...prevState, [field]: value}));
        setErrors(prevErrors => ({...prevErrors, [field]: null}));
    }

    const validateDebitForm = () => {
        if (paymentMethod !== GlobalConstants.PAYMENT_METHOD_DEBIT) {
            return {};
        }
        return {
            accountHolder: (debitForm.accountHolder || "").trim() ? null : Strings.PURCHASE_FORM_FIELD_REQUIRED,
            iban: IBAN.isValid(debitForm.iban || "") ? null : Strings.PURCHASE_FORM_IBAN_INVALID,
            sepaAccepted: debitForm.sepaAccepted ? null : Strings.PURCHASE_FORM_FIELD_REQUIRED,
        };
    }

    const onChangeStep = (step) => {
        switch (step) {
            case 0:
                let url = "/" + GlobalConstants.APP_PATH + "bundles/colors?planVariantId=" + purchaseData.planVariantId + "&componentId=" + purchaseData.componentId + (purchaseData.discountId ? "&discountId=" + purchaseData.discountId : '');
                history.push(url);
                return;
            case 1:
                setShowLoginHint(true);
                return;
            case 3:
            case 4:
                if (purchaseData.maxStep > step - 1) {
                    history.push("/" + GlobalConstants.APP_PATH + "purchase/subscription/confirmation");
                }
                return;
            default:
                //Active step -> Do nothing
                return;
        }
    }

    const proceed = (address) => {
        const updatedPurchaseData = {
            ...purchaseData,
            contact: {...address, email: email != null ? email.email : null},
            validatedAddress: addressKey(address),
            maxStep: 3,
            payment: purchaseData.payment || {},
            paymentMethod: paymentMethod,
            paymentData: paymentMethod === GlobalConstants.PAYMENT_METHOD_DEBIT ? debitForm : purchaseData.paymentData,
        };
        sessionStorage.setItem('ls.purchase', JSON.stringify(updatedPurchaseData));
        setPurchaseData(updatedPurchaseData);
        googleTagManagerWrapper.paymentMethodChangedEvent(updatedPurchaseData, paymentMethod);
        history.push("/" + GlobalConstants.APP_PATH + "purchase/subscription/confirmation");
    }

    const submitFormData = async (e) => {
        e.preventDefault();

        const formErrors = {...validateAddress(contact), country: countryError(contact.country), ...validateDebitForm()};
        setErrors(formErrors);
        setPaymentMethodMissing(paymentMethod == null);
        if (hasErrors(formErrors) || paymentMethod == null) {
            setShowErrorSummary(true);
            //the first problem from the top: the payment method, then the fields in their order
            const firstInvalidField = Object.keys(FIELD_LABELS).find(field => formErrors[field]);
            document.getElementById(paymentMethod == null || !firstInvalidField ? "purchase-payment-methods" : "address-" + firstInvalidField)?.focus();
            return;
        }
        setShowErrorSummary(false);

        //an address the customer already confirmed (e.g. coming back from the confirmation) is not checked again
        if (purchaseData.validatedAddress === addressKey(contact)) {
            proceed(contact);
            return;
        }

        setValidatingAddress(true);
        const response = await CreateResource('api/purchases/address-validations', toValidationRequest(contact), true, GlobalConstants.SPINE_HOST);
        setValidatingAddress(false);

        //if the validation is not available, the address is taken as entered
        const validation = response.error ? null : response.result;
        if (validation != null && (validation.status === "SUGGESTION" || validation.status === "UNCONFIRMED")) {
            setAddressValidation(validation);
            return;
        }
        proceed(contact);
    }

    const acceptSuggestedAddress = () => {
        const address = {...contact, ...fromValidationSuggestion(addressValidation.suggestedAddress)};
        setContact(address);
        setAddressValidation(null);
        proceed(address);
    }

    const keepEnteredAddress = () => {
        setAddressValidation(null);
        proceed(contact);
    }

    const correctEnteredAddress = () => {
        const unconfirmedFields = (addressValidation.unconfirmedFields || [])
            .map(field => VALIDATION_FIELDS[field])
            .filter(field => field != null);
        setWarnings(Object.fromEntries(unconfirmedFields.map(field => [field, Strings.PURCHASE_ADDRESS_WARNING_UNCONFIRMED])));
        //focused after the dialog is closed, while it is open it keeps the focus
        fieldToFocus.current = "address-" + (unconfirmedFields[0] || "street");
        setAddressValidation(null);
    }

    const focusFieldAfterValidation = () => {
        if (fieldToFocus.current) {
            document.getElementById(fieldToFocus.current)?.focus();
            fieldToFocus.current = null;
        }
    }

    const invalidFieldNames = [
        ...(paymentMethodMissing ? [Strings.PURCHASE_DATA_PAYMENT_METHOD_LABEL] : []),
        ...Object.keys(FIELD_LABELS).filter(field => errors[field]).map(field => FIELD_LABELS[field]),
    ];

    return (
        <div className="purchase-container">
            <PurchaseStatus step={2} maxStep={purchaseData != null ? purchaseData.maxStep : 1}
                            bundled={bundled}
                            onChangeStep={(step) => onChangeStep(step)}
            />
            <form className="purchase-page" noValidate onSubmit={submitFormData}>
                <h1 className="purchase-page-title">{Strings.PURCHASE_DATA_TITLE}</h1>
                <p className="purchase-page-info">{bundled ? Strings.PURCHASE_DATA_INFO_BUNDLE : Strings.PURCHASE_DATA_INFO}</p>

                {showErrorSummary && invalidFieldNames.length > 0 &&
                    <div className="purchase-page-error-summary" role="alert">
                        {Strings.PURCHASE_DATA_ERROR_SUMMARY.replace("{0}", invalidFieldNames.join(", "))}
                    </div>
                }

                <div className="purchase-page-section-label" id="purchase-payment-methods-label">
                    {Strings.PURCHASE_DATA_PAYMENT_METHOD_LABEL}
                </div>
                <div id="purchase-payment-methods" className="purchase-page-payment-methods" role="radiogroup"
                     aria-labelledby="purchase-payment-methods-label" tabIndex={-1}>
                    {paymentMethods.map(method =>
                        <div key={method.id}
                             className={"purchase-page-payment-method" + (paymentMethod === method.id ? " selected" : "")}
                             role="radio"
                             aria-checked={paymentMethod === method.id}
                             aria-label={method.name}
                             tabIndex={0}
                             onClick={() => updatePaymentMethod(method.id)}
                             onKeyDown={e => {
                                 if (e.key === "Enter" || e.key === " ") {
                                     e.preventDefault();
                                     updatePaymentMethod(method.id);
                                 }
                             }}>
                            <span className="purchase-page-payment-method-check" aria-hidden="true">✓</span>
                            <img src={method.image} alt=""/>
                            <span className="purchase-page-payment-method-name">{method.name}</span>
                        </div>
                    )}
                </div>
                {paymentMethodMissing &&
                    <div className="purchase-page-message error purchase-page-payment-methods-message">{Strings.PURCHASE_DATA_PAYMENT_METHOD_REQUIRED}</div>
                }

                <div className="purchase-page-section-label">
                    {bundled ? Strings.PURCHASE_DATA_ADDRESS_LABEL_BUNDLE : Strings.PURCHASE_DATA_ADDRESS_LABEL}
                </div>
                <div className="purchase-page-box with-fields">
                    <AddressForm address={contact} errors={errors} warnings={warnings}
                                 onChange={updateContact} onBlur={validateContactField}/>
                </div>

                {paymentMethod === GlobalConstants.PAYMENT_METHOD_DEBIT &&
                    <div className="purchase-page-box with-fields">
                        <div className="purchase-page-field">
                            <label htmlFor="address-accountHolder" className="purchase-page-label">{Strings.PURCHASE_SUBSCRIPTION_DEBIT_OWNER}</label>
                            <input id="address-accountHolder" type="text"
                                   className={"purchase-page-input" + (errors.accountHolder ? " invalid" : "")}
                                   value={debitForm.accountHolder}
                                   onChange={e => updateDebitForm('accountHolder', e.target.value)}/>
                        </div>
                        <div className="purchase-page-field">
                            <label htmlFor="address-iban" className="purchase-page-label">{Strings.PURCHASE_SUBSCRIPTION_DEBIT_IBAN}</label>
                            <input id="address-iban" type="text"
                                   className={"purchase-page-input" + (errors.iban ? " invalid" : "")}
                                   value={debitForm.iban}
                                   onChange={e => updateDebitForm('iban', e.target.value)}/>
                            {errors.iban && <div className="purchase-page-message error">{errors.iban}</div>}
                        </div>
                        <div className="purchase-page-field">
                            <label htmlFor="address-bic" className="purchase-page-label">{Strings.PURCHASE_SUBSCRIPTION_DEBIT_BIC}</label>
                            <input id="address-bic" type="text" className="purchase-page-input"
                                   value={debitForm.bic}
                                   onChange={e => updateDebitForm('bic', e.target.value)}/>
                        </div>
                        <label className="purchase-page-checkbox">
                            <input id="address-sepaAccepted" type="checkbox" checked={!!debitForm.sepaAccepted}
                                   onChange={e => updateDebitForm('sepaAccepted', e.target.checked)}/>
                            <span>
                                <strong>{Strings.PURCHASE_SUBSCRIPTION_DEBIT_AGB_TITLE}</strong><br/>
                                {Strings.PURCHASE_SUBSCRIPTION_DEBIT_AGB_INFO}
                            </span>
                        </label>
                    </div>
                }

                <button type="submit" className="purchase-page-button" disabled={validatingAddress}>
                    {validatingAddress ? Strings.PURCHASE_DATA_VALIDATING_ADDRESS : Strings.PURCHASE_PROCEED_BUTTON}
                </button>
            </form>

            <AddressValidationModal validation={addressValidation}
                                    enteredAddress={contact}
                                    onAcceptSuggestion={acceptSuggestedAddress}
                                    onKeepInput={keepEnteredAddress}
                                    onCorrectInput={correctEnteredAddress}
                                    onExited={focusFieldAfterValidation}/>

            <ConfirmationModal
                title={Strings.PURCHASE_REAUTHENTICATE_TITLE}
                text={Strings.PURCHASE_REAUTHENTICATE_HINT}
                actionButtonLabel={Strings.PURCHASE_REAUTHENTICATE_BUTTON}
                actionButtonVariant="danger"
                onActionPress={() => {
                    localStorage.removeItem("ls.token");
                    history.push("/" + GlobalConstants.APP_PATH + "purchase/" + purchaseData.planVariantId + '?selectedColor=' + (purchaseData.selectedColor ? purchaseData.selectedColor : '') + "&componentId=" + purchaseData.componentId + (purchaseData.discountId ? "&discountId=" + purchaseData.discountId : '') + (purchaseData.selectedGtin ? "&selectedGtin=" + purchaseData.selectedGtin : ''))
                }}
                show={showLoginHint}
                onHide={() => setShowLoginHint(false)}
            />

            <InfoModal
                title={Strings.PURCHASE_TARIF_PURCHASE_ERROR_TITLE}
                text={Strings.PURCHASE_TARIF_PURCHASE_ERROR_TEXT}
                show={showPlanPurchaseError}
                onHide={() => {
                    googleTagManagerWrapper.event('cancelPurchase', {reason: 'noTarifAvailable'});
                    googleTagManagerWrapper.noPurchase();
                    history.push("/" + GlobalConstants.APP_PATH + "premium");
                    return null;
                }}/>

            <InfoModal
                title={Strings.PURCHASE_CUSTOMER_LOCKED_ERROR_TITLE}
                text={Strings.PURCHASE_CUSTOMER_LOCKED_ERROR_TEXT}
                show={showCustomerLockedError}
                closeButtonLabel={Strings.PURCHASE_CUSTOMER_LOCKED_ERROR_BUTTON}
                onHide={() => {
                    googleTagManagerWrapper.event('cancelPurchase', {reason: 'Customer is locked in Billwerk'});
                    googleTagManagerWrapper.noPurchase();
                    window.location.href = "https://helpcenter.tiger.media/hc/de";
                    return null;
                }}/>
        </div>
    )
}
